import { useState, useEffect } from 'react';
import { X, Check, Upload, Link as LinkIcon, Music, ArrowLeft } from 'lucide-react';
import { api, UserProfile, RoleKey, PortfolioItem } from '../../lib/api';
import { haptic } from '../../lib/telegram';

interface EditProfileWizardProps {
  profile: UserProfile;
  onComplete: (profile: UserProfile) => void;
  onClose?: () => void;
}

const ROLES: Array<{ id: RoleKey; label: string }> = [
  { id: 'listener', label: 'Listener' },
  { id: 'beatmaker', label: 'Beatmaker' },
  { id: 'producer', label: 'Producer' },
  { id: 'designer', label: 'Designer' },
  { id: 'performer', label: 'Performer' },
  { id: 'director', label: 'Director' },
];

export default function EditProfileWizard({ profile, onComplete, onClose }: EditProfileWizardProps) {
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfile>>({
    name: profile.name || '',
    photo_file_id: profile.photo_file_id,
    roles: profile.roles || '',
    city: profile.city || '',
    styles: profile.styles || '',
    bio: profile.bio || '',
    portfolios: profile.portfolios || [],
    social_links: profile.social_links || {},
  });
  
  const [roles, setRoles] = useState<RoleKey[]>(
    profile.roles ? String(profile.roles).split(',') as RoleKey[] : []
  );

  // Update roles when profile changes
  useEffect(() => {
    if (profile.roles) {
      setRoles(String(profile.roles).split(',') as RoleKey[]);
    }
  }, [profile.roles]);
  
  const [errors, setErrors] = useState<string>('');
  const [portfolioType, setPortfolioType] = useState<'link' | 'photo' | 'video' | 'audio' | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setErrors('');
    setIsSaving(true);
    try {
      // Validate fields
      if (!editedProfile.name?.trim() || editedProfile.name.trim().length < 2 || editedProfile.name.trim().length > 50) {
        setErrors('Імʼя має бути від 2 до 50 символів');
        haptic.light();
        return;
      }
      if (roles.length === 0) {
        setErrors('Оберіть хоча б одну роль');
        haptic.light();
        return;
      }
      // Consolidate profile updates
      const profileDataToUpdate = {
        name: editedProfile.name.trim(),
        roles: roles.join(','),
        city: editedProfile.city?.trim() || 'Не вказано',
        styles: editedProfile.styles?.trim() || 'Не вказано',
        bio: editedProfile.bio?.trim() || 'Не вказано',
        photo_file_id: editedProfile.photo_file_id || undefined,
        social_links: editedProfile.social_links,
      };
      await api.profile.update(profileDataToUpdate);
      // Update portfolio separately
      await api.profile.updatePortfolio(editedProfile.portfolios || []);
      haptic.success();
      // Fetch the latest profile and call onComplete
      const finalProfile = await api.profile.get();
      onComplete(finalProfile);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setErrors('Помилка при збереженні. Спробуйте ще раз.');
      haptic.error();
    } finally {
      setIsSaving(false);
    }
  };

  const toggleRole = (roleId: RoleKey) => {
    if (roleId === 'listener') {
      if (roles.includes('listener')) {
        setRoles([]);
      } else {
        setRoles(['listener']);
      }
    } else {
      const newRoles = roles.filter(r => r !== 'listener');
      if (newRoles.includes(roleId)) {
        setRoles(newRoles.filter(r => r !== roleId));
      } else {
        setRoles([...newRoles, roleId]);
      }
    }
    setErrors('');
  };

  const addPortfolioItem = (type: 'link' | 'photo' | 'video' | 'audio', value: string) => {
    if (!value.trim()) {
      setErrors('Введіть значення для портфоліо');
      haptic.light();
      return;
    }
    if ((editedProfile.portfolios?.length || 0) >= 3) {
      setErrors('Максимум 3 елементи портфоліо');
      haptic.light();
      return;
    }
    const newItem: PortfolioItem = {
      type: type,
      value: value.trim(),
    };
    setEditedProfile({
      ...editedProfile,
      portfolios: [...(editedProfile.portfolios || []), newItem],
    });
    setPortfolioType(null);
    setErrors('');
    haptic.light();
  };

  const removePortfolioItem = (index: number) => {
    const newPortfolios = [...(editedProfile.portfolios || [])];
    newPortfolios.splice(index, 1);
    setEditedProfile({ ...editedProfile, portfolios: newPortfolios });
    haptic.light();
  };

  const handleProfilePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setErrors('');
      haptic.light();

      const uploaded = await api.profile.uploadPortfolioFile(file, 'Фото профілю');

      const newPortfolioItem: PortfolioItem = {
        type: uploaded.type as PortfolioItem['type'],
        value: uploaded.url,
        preview_url: uploaded.url,
        cover_url: uploaded.url,
      };

      setEditedProfile((prev) => ({
        ...prev,
        photo_file_id: uploaded.url,
        portfolios: [...(prev.portfolios || []), newPortfolioItem],
      }));
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      setErrors('Не вдалося завантажити фото. Спробуйте ще раз.');
      haptic.error();
    } finally {
      event.target.value = '';
    }
  };

  const [tempPortfolioValue, setTempPortfolioValue] = useState('');
  const currentRoles = editedProfile.roles ? String(editedProfile.roles).split(',') as RoleKey[] : [];
  const isListener = currentRoles.length === 1 && currentRoles[0] === 'listener';
  return (
    <div className="min-h-screen bg-splyt-gradient flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-4 border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-10">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
        <h1 className="text-xl font-bold text-white">Редагування профілю</h1>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 flex-1 overflow-y-auto pb-24">
        {/* General Info Section */}
        <div className="space-y-3">
          <p className="text-xs text-white/50 px-2">ОСНОВНА ІНФОРМАЦІЯ</p>
          <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
            {/* Name */}
            <input
              type="text"
              value={editedProfile.name}
              onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
              placeholder="Ім'я"
              className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
            />
            {/* City */}
            <input
              type="text"
              value={editedProfile.city}
              onChange={(e) => setEditedProfile({ ...editedProfile, city: e.target.value })}
              placeholder="Місто"
              className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
            />
          </div>
        </div>

        {/* Photo Section */}
        <div className="space-y-3">
          <p className="text-xs text-white/50 px-2">ФОТО ПРОФІЛЮ</p>
          <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
            <label className="w-full flex items-center justify-center h-32 rounded-xl border-2 border-dashed border-white/20 hover:border-splyt-green transition-all cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handleProfilePhotoChange} />
              {editedProfile.photo_file_id ? (
                <img src={editedProfile.photo_file_id} alt="Profile" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <div className="text-center text-white/50">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p>Завантажити фото</p>
                </div>
              )}
            </label>
          </div>
        </div>

        {/* Roles Section */}
        <div className="space-y-3">
          <p className="text-xs text-white/50 px-2">РОЛІ</p>
          <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
            <div className="grid grid-cols-2 gap-3">
              {ROLES.map((role) => {
                const isSelected = roles.includes(role.id);
                return (
                  <button
                    key={role.id}
                    onClick={() => toggleRole(role.id)}
                    className={`h-24 rounded-xl border transition-all ${isSelected ? 'border-splyt-green bg-splyt-green/10' : 'border-white/10 bg-black/20'}`}
                  >
                    <p className={`font-bold ${isSelected ? 'text-splyt-green' : 'text-white'}`}>{role.label}</p>
                    {isSelected && <Check className="w-5 h-5 absolute top-2 right-2 text-splyt-green" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Details Section (conditional) */}
        {!isListener && (
          <>
            <div className="space-y-3">
              <p className="text-xs text-white/50 px-2">ДЕТАЛІ</p>
              <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
                <input
                  type="text"
                  value={editedProfile.styles}
                  onChange={(e) => setEditedProfile({ ...editedProfile, styles: e.target.value })}
                  placeholder="Стилі (trap, hyperpop...)"
                  className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                />
                <textarea
                  value={editedProfile.bio}
                  onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                  placeholder="Біо (до 250 символів)"
                  rows={4}
                  className="w-full p-4 rounded-xl border bg-black/20 border-white/10 text-white resize-none focus:outline-none focus:border-splyt-green"
                />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-white/50 px-2">ПОРТФОЛІО ({editedProfile.portfolios?.length || 0}/3)</p>
              <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
                {editedProfile.portfolios?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-black/20">
                    <span className="text-splyt-green">{item.type === 'link' ? <LinkIcon size={16} /> : <Music size={16} />}</span>
                    <p className="flex-1 truncate text-white/80">{item.value}</p>
                    <button onClick={() => removePortfolioItem(index)} className="text-red-500 hover:text-red-400">
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {(editedProfile.portfolios?.length || 0) < 3 && (
                  <div className="flex gap-2">
                    <select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPortfolioType(e.target.value as 'link' | 'photo' | 'video' | 'audio')}
                      className="h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                    >
                      <option value="link">Link</option>
                      <option value="audio">Audio</option>
                      <option value="video">Video</option>
                      <option value="photo">Photo</option>
                    </select>
                    <input
                      type="text"
                      value={tempPortfolioValue}
                      onChange={(e) => setTempPortfolioValue(e.target.value)}
                      placeholder="URL або посилання"
                      className="flex-1 h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                    />
                    <button onClick={() => addPortfolioItem(portfolioType || 'link', tempPortfolioValue)} className="w-12 h-12 rounded-xl bg-splyt-green text-black flex items-center justify-center">
                      <Check />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs text-white/50 px-2">СОЦІАЛЬНІ МЕРЕЖІ</p>
              <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
                <input
                  type="text"
                  value={editedProfile.social_links?.instagram || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, social_links: { ...editedProfile.social_links, instagram: e.target.value } })}
                  placeholder="Instagram"
                  className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                />
                <input
                  type="text"
                  value={editedProfile.social_links?.soundcloud || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, social_links: { ...editedProfile.social_links, soundcloud: e.target.value } })}
                  placeholder="SoundCloud"
                  className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                />
                <input
                  type="text"
                  value={editedProfile.social_links?.spotify || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, social_links: { ...editedProfile.social_links, spotify: e.target.value } })}
                  placeholder="Spotify"
                  className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                />
                <input
                  type="text"
                  value={editedProfile.social_links?.youtube || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, social_links: { ...editedProfile.social_links, youtube: e.target.value } })}
                  placeholder="YouTube"
                  className="w-full h-12 px-4 rounded-xl border bg-black/20 border-white/10 text-white focus:outline-none focus:border-splyt-green"
                />
              </div>
            </div>
          </>
        )}

        {errors && (
          <div className="p-3 rounded-xl border bg-red-500/10 border-red-500/30">
            <p className="text-red-400 text-sm">{errors}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-20 p-4 border-t border-white/10 bg-gradient-to-t from-black to-transparent">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full py-4 rounded-xl flex items-center justify-center gap-2 transition-all text-white font-bold bg-splyt-green disabled:opacity-50"
        >
          {isSaving ? 'Збереження...' : 'Зберегти'}
        </button>
      </div>
    </div>
  );
}

